import {
  Background,
  ConnectionLineType,
  Controls,
  ReactFlow,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { useMemo } from "react";
import { Person } from "../../../__generated__/graphql";
import FamilyTreePerson from "./Person";
import styles from "./Tree.module.scss";
import {
  getLayoutedElements,
  getPersonNode,
  nodeHeight,
  nodeWidth,
} from "./utils";

interface FamilyTree {
  person: Person;
}

const FamilyTree = ({ person }: FamilyTree) => {
  const { setCenter, getZoom } = useReactFlow();
  const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
    [getPersonNode(person.id)],
    [],
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(layoutedNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(layoutedEdges);

  const nodeTypes = useMemo(
    () => ({
      custom: (props) => (
        <FamilyTreePerson
          {...props}
          onShowParents={(fatherId, motherId) => {
            const newEdges = [
              ...edges,
              ...(fatherId
                ? [
                    {
                      id: `${fatherId}-${props.id}`,
                      source: fatherId,
                      target: props.id,
                    },
                  ]
                : []),
              ...(motherId
                ? [
                    {
                      id: `${motherId}-${props.id}`,
                      source: motherId,
                      target: props.id,
                    },
                  ]
                : []),
            ];
            const layoutedNodes = getLayoutedElements(
              [
                ...nodes,
                ...(props.id
                  ? [getPersonNode(props.id, props.data.hideChildren, true)]
                  : []),
                ...(fatherId ? [getPersonNode(fatherId, true, false)] : []),
                ...(motherId ? [getPersonNode(motherId, true, false)] : []),
              ],
              newEdges,
            ).nodes;
            setNodes(layoutedNodes);
            setEdges(newEdges);
            setCenter(
              (layoutedNodes.find((x) => x.id === props.id)?.position.x ?? 0) +
                nodeWidth / 2,
              (layoutedNodes.find((x) => x.id === props.id)?.position.y ?? 0) +
                nodeHeight / 2,
              {
                zoom: getZoom(),
              },
            );
          }}
          onShowChildren={(children) => {
            const newEdges = [
              ...edges,
              ...(children.length > 0
                ? children.map((x) => ({
                    id: `${x}-${props.id}`,
                    source: props.id,
                    target: x,
                  }))
                : []),
            ];
            const layoutedNodes = getLayoutedElements(
              [
                ...nodes,
                ...(props.id
                  ? [getPersonNode(props.id, true, props.data.hideParents)]
                  : []),
                ...(children.length > 0
                  ? children.map((x) => getPersonNode(x, false, true))
                  : []),
              ],
              newEdges,
            ).nodes;
            setNodes(layoutedNodes);
            setEdges(newEdges);
            setCenter(
              (layoutedNodes.find((x) => x.id === props.id)?.position.x ?? 0) +
                nodeWidth / 2,
              (layoutedNodes.find((x) => x.id === props.id)?.position.y ?? 0) +
                nodeHeight / 2,
              {
                zoom: getZoom(),
              },
            );
          }}
        />
      ),
    }),
    [setEdges, setNodes, edges, nodes, setCenter, getZoom],
  );

  return (
    <div className={styles.familyTree}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        connectionLineType={ConnectionLineType.SmoothStep}
        fitView
        nodesDraggable={false}
        nodesConnectable={false}
        proOptions={{ hideAttribution: true }}
        style={{ backgroundColor: "#f0f0ea" }}
        zoomOnScroll={false}
        maxZoom={1}
        edgesFocusable={false}
      >
        <Controls
          showFitView={false}
          showInteractive={false}
          position="bottom-right"
        />
        <Background />
      </ReactFlow>
    </div>
  );
};

export default FamilyTree;
